import { FormActionFrameworkStep, registerStep } from '../step';
import { resolveTemplate } from '../template';
import { resolveFieldName } from './populateDropdown';
const DEFAULT_CENTER = { lat: 39.8283, lng: -98.5795 };
let mapsLoadPromise = null;
/**
 * Demo-only address autocomplete.
 * Keeps the SurveyJS question/model intact, hides only its rendered content,
 * and places a Google autocomplete input in the same question container.
 */
export class GoogleAddressStep extends FormActionFrameworkStep {
    constructor() {
        super(...arguments);
        Object.defineProperty(this, "type", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 'googleAddress'
        });
        Object.defineProperty(this, "config", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: {
                apiKey: '',
                fieldName: '',
                placeholder: 'Start typing an address…',
                country: '',
                allowMapSelect: false,
                mapCenter: '',
            }
        });
    }
    async execute(context) {
        const apiKey = String(resolveTemplate(this.config.apiKey ?? '', context) ?? '').trim();
        const fieldName = resolveFieldName({
            fieldName: String(resolveTemplate(this.config.fieldName ?? '', context) ?? ''),
        });
        const placeholder = String(resolveTemplate(this.config.placeholder ?? '', context) ??
            'Start typing an address…');
        const country = String(resolveTemplate(this.config.country ?? '', context) ?? '').trim();
        const allowMapSelect = Boolean(this.config.allowMapSelect);
        const mapCenterRaw = String(resolveTemplate(this.config.mapCenter ?? '', context) ?? '').trim();
        if (!apiKey) {
            context.log('[googleAddress] missing apiKey — skipped');
            return context;
        }
        if (!fieldName) {
            context.log('[googleAddress] missing fieldName — skipped');
            return context;
        }
        if (typeof document === 'undefined') {
            context.log('[googleAddress] no DOM available — skipped');
            return context;
        }
        const questionElement = await waitForQuestionElement(fieldName);
        if (!questionElement) {
            context.log(`[googleAddress] element not found for data-name="${fieldName}" — skipped`);
            return context;
        }
        const originalContent = questionElement.querySelector('.sd-element__content, .sd-question__content');
        if (!originalContent) {
            context.log(`[googleAddress] rendered content not found for ${fieldName} — skipped`);
            return context;
        }
        questionElement
            .querySelectorAll(':scope > .faf-google-address')
            .forEach((element) => element.remove());
        originalContent.hidden = true;
        originalContent.dataset.fafGoogleAddressOriginal = '1';
        const wrapper = document.createElement('div');
        wrapper.className = 'faf-google-address';
        const row = document.createElement('div');
        row.className = 'faf-google-address-row';
        const input = document.createElement('input');
        input.type = 'text';
        input.className = 'faf-google-address-input';
        input.placeholder = placeholder;
        input.autocomplete = 'street-address';
        input.setAttribute('aria-label', placeholder || 'Find address');
        row.appendChild(input);
        let mapButton = null;
        if (allowMapSelect) {
            mapButton = document.createElement('button');
            mapButton.type = 'button';
            mapButton.className = 'faf-google-address-map-btn';
            mapButton.title = 'Select on map';
            mapButton.setAttribute('aria-label', 'Select on map');
            mapButton.textContent = 'Map';
            row.appendChild(mapButton);
        }
        wrapper.appendChild(row);
        originalContent.insertAdjacentElement('afterend', wrapper);
        const model = context.form;
        const question = model?.getQuestionByName?.(fieldName);
        const initialValue = question?.value;
        if (initialValue != null)
            input.value = String(initialValue);
        const setSurveyValue = (value) => {
            if (typeof model?.setValue === 'function') {
                model.setValue(fieldName, value);
            }
            else if (question) {
                question.value = value;
            }
            context.formValues = { ...context.formValues, [fieldName]: value };
        };
        input.addEventListener('change', () => setSurveyValue(input.value));
        if (allowMapSelect && mapButton) {
            const openMap = async () => {
                mapButton.disabled = true;
                mapButton.textContent = 'Loading…';
                try {
                    const google = await loadGoogleMaps(apiKey);
                    await openMapPicker({
                        google,
                        input,
                        setSurveyValue,
                        country,
                        mapCenter: parseLatLng(mapCenterRaw) ?? DEFAULT_CENTER,
                        log: (message) => context.log(message),
                    });
                }
                catch (err) {
                    const message = err instanceof Error ? err.message : String(err);
                    context.log(`[googleAddress] map picker failed: ${message}`);
                    console.error('[googleAddress] map picker failed', err);
                    wrapper.classList.add('has-error');
                }
                finally {
                    mapButton.disabled = false;
                    mapButton.textContent = 'Map';
                }
            };
            mapButton.addEventListener('click', (event) => {
                event.preventDefault();
                event.stopPropagation();
                void openMap();
            });
            // Clicking an empty field also opens the map; populated fields remain editable.
            input.addEventListener('click', () => {
                if (!input.value.trim())
                    void openMap();
            });
            context.log(`[googleAddress] map select enabled for ${fieldName}`);
        }
        try {
            const google = await loadGoogleMaps(apiKey);
            const options = {
                fields: ['formatted_address', 'name', 'geometry'],
                types: ['address'],
            };
            if (country) {
                options.componentRestrictions = { country };
            }
            const autocomplete = new google.maps.places.Autocomplete(input, options);
            autocomplete.addListener('place_changed', () => {
                const place = autocomplete.getPlace();
                const value = place.formatted_address || place.name || input.value;
                input.value = value;
                setSurveyValue(value);
            });
            context.log(`[googleAddress] attached address autocomplete to ${fieldName}`);
        }
        catch (err) {
            const message = err instanceof Error ? err.message : String(err);
            context.log(`[googleAddress] Google Maps failed to load: ${message}`);
            wrapper.classList.add('has-error');
        }
        return context;
    }
    showStepForm(container, _context, onUpdated) {
        const cfg = this.config;
        const allowMapSelect = Boolean(cfg.allowMapSelect);
        container.innerHTML = `
      <div class="mb-3">
        <label class="form-label">Step name</label>
        <input type="text" class="form-control" data-faf="name" value="${escapeAttr(this.name)}" />
      </div>
      <div class="mb-3">
        <label class="form-label">Google Maps API key</label>
        <input type="password" class="form-control" data-faf="apiKey" value="${escapeAttr(cfg.apiKey)}" autocomplete="off" />
        <div class="text-muted small">This key is loaded in the browser and cannot be kept secret. Restrict it by domain and API.</div>
      </div>
      <div class="mb-3">
        <label class="form-label">Field name (SurveyJS name / data-name)</label>
        <input type="text" class="form-control" data-faf="fieldName" value="${escapeAttr(cfg.fieldName)}" placeholder="surveyjs_data_name" />
      </div>
      <div class="faf-id-row mb-3">
        <div class="faf-id-field">
          <label class="form-label">Placeholder</label>
          <input type="text" class="form-control" data-faf="placeholder" value="${escapeAttr(cfg.placeholder)}" />
        </div>
        <div class="faf-id-field">
          <label class="form-label">Country (optional)</label>
          <input type="text" class="form-control" data-faf="country" value="${escapeAttr(cfg.country)}" placeholder="us" />
        </div>
      </div>
      <div class="faf-id-row mb-3">
        <div class="faf-id-field">
          <label class="form-label">Allow select on map</label>
          <select class="form-control" data-faf="allowMapSelect">
            <option value="false"${!allowMapSelect ? ' selected' : ''}>No</option>
            <option value="true"${allowMapSelect ? ' selected' : ''}>Yes</option>
          </select>
        </div>
        <div class="faf-id-field">
          <label class="form-label">Map center (lat,lng)</label>
          <input type="text" class="form-control" data-faf="mapCenter" value="${escapeAttr(cfg.mapCenter)}" placeholder="39.8283,-98.5795" />
        </div>
      </div>
      <div class="text-muted small">All text fields support {{ }} expressions. Map select adds a Map button and opens a clickable map picker.</div>
    `;
        const sync = () => {
            this.name = inputValue(container, 'name');
            this.config.apiKey = inputValue(container, 'apiKey');
            this.config.fieldName = inputValue(container, 'fieldName');
            this.config.placeholder = inputValue(container, 'placeholder');
            this.config.country = inputValue(container, 'country');
            this.config.allowMapSelect = selectValue(container, 'allowMapSelect') === 'true';
            this.config.mapCenter = inputValue(container, 'mapCenter');
            onUpdated?.();
        };
        container.querySelectorAll('input, select').forEach((element) => {
            element.addEventListener('change', sync);
            element.addEventListener('blur', sync);
        });
    }
}
async function openMapPicker({ google, input, setSurveyValue, country, mapCenter, log, }) {
    // One picker at a time.
    document.querySelectorAll('.faf-google-map-overlay').forEach((el) => el.remove());
    const overlay = document.createElement('div');
    overlay.className = 'faf-google-map-overlay';
    overlay.innerHTML = `
    <div class="faf-google-map-dialog" role="dialog" aria-modal="true" aria-label="Select address on map">
      <div class="faf-google-map-dialog-head">
        <strong>Select on map</strong>
        <span class="faf-google-map-hint">Click the map to place a pin</span>
      </div>
      <div class="faf-google-map-canvas"></div>
      <div class="faf-google-map-preview" aria-live="polite">Click the map to choose a location…</div>
      <div class="faf-google-map-actions">
        <button type="button" class="btn btn-ghost" data-faf-map="cancel">Cancel</button>
        <button type="button" class="btn btn-primary" data-faf-map="use" disabled>Use address</button>
      </div>
    </div>
  `;
    document.body.appendChild(overlay);
    const canvas = overlay.querySelector('.faf-google-map-canvas');
    const preview = overlay.querySelector('.faf-google-map-preview');
    const useBtn = overlay.querySelector('[data-faf-map="use"]');
    const cancelBtn = overlay.querySelector('[data-faf-map="cancel"]');
    let selectedAddress = '';
    let selectedPosition = null;
    const close = () => overlay.remove();
    cancelBtn.addEventListener('click', close);
    overlay.addEventListener('click', (event) => {
        if (event.target === overlay)
            close();
    });
    const map = new google.maps.Map(canvas, {
        center: mapCenter,
        zoom: 4,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
    });
    const marker = new google.maps.Marker({
        map,
        draggable: true,
        position: mapCenter,
    });
    // Hide until first click so the default center isn't treated as a pick.
    marker.setMap(null);
    const geocoder = new google.maps.Geocoder();
    const reverseGeocode = (position) => {
        preview.textContent = 'Looking up address…';
        useBtn.disabled = true;
        selectedAddress = '';
        selectedPosition = position;
        const request = { location: position };
        if (country) {
            request.componentRestrictions = { country };
        }
        geocoder.geocode(request, (results, status) => {
            if (status !== 'OK' || !results?.length) {
                preview.textContent = `Could not resolve address (${status}). You can still type in the field.`;
                log(`[googleAddress] reverse geocode failed: ${status}`);
                return;
            }
            selectedAddress = results[0].formatted_address || '';
            preview.textContent = selectedAddress || 'Address found';
            useBtn.disabled = !selectedAddress;
        });
    };
    // Prefer centering on the current input address if we can geocode it.
    if (input.value.trim()) {
        geocoder.geocode({ address: input.value.trim() }, (results, status) => {
            if (status === 'OK' && results?.[0]?.geometry?.location) {
                const loc = results[0].geometry.location;
                const position = { lat: loc.lat(), lng: loc.lng() };
                map.setCenter(position);
                map.setZoom(15);
                marker.setMap(map);
                marker.setPosition(position);
                selectedAddress = results[0].formatted_address || input.value;
                selectedPosition = position;
                preview.textContent = selectedAddress;
                useBtn.disabled = !selectedAddress;
            }
        });
    }
    map.addListener('click', (event) => {
        if (!event.latLng)
            return;
        const position = { lat: event.latLng.lat(), lng: event.latLng.lng() };
        marker.setMap(map);
        marker.setPosition(position);
        map.panTo(position);
        reverseGeocode(position);
    });
    // Allow dragging the pin too.
    // Marker drag events aren't typed on our stub; hook via DOM-less google listener pattern.
    marker.addListener('dragend', () => {
        const pos = marker.getPosition();
        if (!pos)
            return;
        reverseGeocode({ lat: pos.lat(), lng: pos.lng() });
    });
    useBtn.addEventListener('click', () => {
        if (!selectedAddress)
            return;
        input.value = selectedAddress;
        setSurveyValue(selectedAddress);
        log(`[googleAddress] map selection → ${selectedAddress}` +
            (selectedPosition
                ? ` (${selectedPosition.lat.toFixed(5)}, ${selectedPosition.lng.toFixed(5)})`
                : ''));
        close();
    });
    // Force a resize after the overlay is painted so tiles fill the canvas.
    requestAnimationFrame(() => {
        google.maps.event.trigger(map, 'resize');
        map.setCenter(mapCenter);
    });
}
function parseLatLng(raw) {
    if (!raw)
        return null;
    const parts = raw.split(',').map((part) => part.trim());
    if (parts.length !== 2)
        return null;
    const lat = Number(parts[0]);
    const lng = Number(parts[1]);
    if (!Number.isFinite(lat) || !Number.isFinite(lng))
        return null;
    return { lat, lng };
}
function loadGoogleMaps(apiKey) {
    const existing = getGoogleMaps();
    if (existing?.maps?.places?.Autocomplete && existing.maps.Map) {
        return Promise.resolve(existing);
    }
    if (mapsLoadPromise)
        return mapsLoadPromise;
    mapsLoadPromise = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src =
            `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}` +
                '&libraries=places&loading=async';
        script.async = true;
        script.defer = true;
        script.onload = () => {
            const google = getGoogleMaps();
            if (google?.maps?.places?.Autocomplete && google.maps.Map)
                resolve(google);
            else
                reject(new Error('Google Maps Places/Map library was unavailable'));
        };
        script.onerror = () => reject(new Error('Google Maps script request failed'));
        document.head.appendChild(script);
    }).catch((err) => {
        mapsLoadPromise = null;
        throw err;
    });
    return mapsLoadPromise;
}
function getGoogleMaps() {
    return window.google;
}
/** SurveyJS may mount a frame after formLoad begins, so briefly wait for its DOM. */
async function waitForQuestionElement(fieldName, timeoutMs = 2000) {
    const selector = `[data-name="${cssEscape(fieldName)}"]`;
    const startedAt = Date.now();
    while (Date.now() - startedAt < timeoutMs) {
        const element = document.querySelector(selector);
        if (element)
            return element;
        await new Promise((resolve) => requestAnimationFrame(() => resolve()));
    }
    return document.querySelector(selector);
}
function inputValue(container, key) {
    const element = container.querySelector(`[data-faf="${key}"]`);
    return element?.value?.trim() ?? '';
}
function selectValue(container, key) {
    const element = container.querySelector(`[data-faf="${key}"]`);
    return element?.value ?? '';
}
function cssEscape(value) {
    return value.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}
function escapeAttr(value) {
    if (value == null)
        return '';
    return String(value)
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/</g, '&lt;');
}
registerStep('googleAddress', GoogleAddressStep);
